"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardCheck, PackageOpen, SearchCheck, Sparkles, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  title: "Initial Sketch",
  description: "Every masterpiece begins with a hand-drawn visual. We capture your vision on paper, defining the artistic soul and basic proportions of your custom jewelry piece.",
  icon: ClipboardCheck
}, {
  title: "Digital Blueprint",
  description: "We translate the sketch into precise 2D CAD lines. This technical foundation ensures structural integrity and exact measurements for every setting and prong.",
  icon: PackageOpen
}, {
  title: "3D Rendering Preview",
  description: "Your design is rendered as a photorealistic 3D preview. This high-resolution visualization allows you to see the final piece in its intended form before 3D printing and production.",
  icon: Sparkles
}, {
  title: "Wax Prototype",
  description: "The final digital model is 3D printed into a high-resolution wax sample. This physical prototype allows for a final tactile inspection of the design's perfection.",
  icon: SearchCheck
}];
export default function ThePerfectProtocol() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const currentScrollPosition = windowHeight / 2 - rect.top;
      let percentage = currentScrollPosition / totalScrollableDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222853" id="the-crafting-workflow" ref={sectionRef} className="bg-background text-foreground relative min-h-screen">
      {}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 py-4 px-6 shadow-md">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-default font-bold tracking-tight text-primary">
              The Crafting Workflow
            </h2>
            <span className="text-sm font-mono text-secondary">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-1.5 bg-muted" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="text-center mb-24">
          <h3 className="text-3xl md:text-5xl font-serif font-bold text-primary mb-6 tracking-tighter">
            Digital Precision, <br className="hidden md:block" /> Artisanal Soul.
          </h3>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            Our workflow bridges the gap between imagination and reality. Explore the four-stage 
            evolution from a simple concept to a high-precision 3D printed prototype.
          </p>
        </div>

        {}
        <div className="relative border-l-2 border-secondary/20 ml-4 md:ml-12 space-y-32 pb-24">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative pl-8 md:pl-20 min-h-[30vh] flex flex-col justify-center group">
                {}
                <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 size-5 rounded-md bg-background border-4 border-secondary transition-transform duration-500 group-hover:scale-125 group-hover:border-primary" />
                
                {}
                <span className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 text-[8rem] md:text-[14rem] font-serif font-bold text-primary/5 select-none leading-none pointer-events-none transition-colors duration-500 group-hover:text-secondary/10">
                  {i + 1}
                </span>

                <Card className="relative z-10 border-none bg-transparent overflow-visible">
                  <CardHeader className="px-0 pt-0 pb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 rounded-md bg-card text-secondary shadow-lg border border-border/50">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-2xl md:text-3xl font-default font-bold text-primary">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl font-default">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center relative z-20">
          <Button asChild size="lg" className="rounded-md px-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-default font-semibold shadow-xl transition-all hover:scale-105">
            <Link to="/StartYourProjectPage">
              Start Your Design
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}